import { DOCUMENT } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';

import { FeatureFlagService } from '@mimecast-ui/shared/data-access-core/feature-flags';
import { FeedbackModule } from '@mimecast-ui/ui-core/feedback';
import { WINDOW } from '@mimecast-ui/ui-core/window';
import { loginApiInterceptor } from '@mimecast-ui/universal-login/data-access-auth';
import {
  UniversalLoginBrandingService,
  provideLoginConfig,
} from '@mimecast-ui/universal-login/data-access-config';

import { appRoutes } from './app.routes';
import { provideMulFeatureFlags } from './launch-darkly/provide-mul-feature-flags';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    { provide: WINDOW, useFactory: () => window },
    CookieService,
    provideHttpClient(withInterceptors([loginApiInterceptor])),
    // Config must initialize before routes render. LaunchDarkly runs after this.
    provideLoginConfig(),
    // Branding API resolves before first paint so branded CSS vars / favicon / title
    // apply with no flash of Mimecast defaults (same pattern as TTPWP).
    provideAppInitializer(() => {
      const brandingService = inject(UniversalLoginBrandingService);
      const document = inject(DOCUMENT);
      const pageUrl = document.defaultView?.location.href ?? '';
      return brandingService.initializeFromUrl(pageUrl);
    }),
    // FeedbackModule provides SnackBarService, which FeatureFlagService uses to surface
    // an error notification when the LD request fails. Wired here so that LD failures
    // are visible to the user without blocking the login flow.
    importProvidersFrom(FeedbackModule),
    provideMulFeatureFlags(),
    // Step 2: Start LD - fire-and-forget so bootstrap is never delayed by a slow or
    // unavailable LaunchDarkly endpoint. Factory observables above pipe from
    // configLoaded$ so LD reads ConfigService.grid only after the config request
    // has completed. An LD outage never blocks the login flow (catchError in
    // FeatureFlagService degrades gracefully to defaultFlags; all MUL flags
    // default to false).
    provideAppInitializer(() => {
      inject(FeatureFlagService).initialize();
    }),
    provideRouter(appRoutes),
  ],
};
